import { Badge } from "@d12labs/ui/badge";
import { Check, Copy, User } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Avatar } from "./avatar";

// ── Parser ────────────────────────────────────────────────────────────────────

export interface ActionItemEntry {
	text: string;
	ts: string | null;
	sec: number | null;
}

export interface ActionItemGroup {
	owner: string;
	unassigned: boolean;
	items: ActionItemEntry[];
}

/**
 * Parse the raw action-items markdown blob emitted by Fireflies/AI into a
 * grouped-by-owner structure.
 *
 * Expected format:
 *   \n**Owner Name**\n<task text> (MM:SS)\n<task text>\n\n**Next Owner**\n...
 *
 * Rules:
 * - Groups start at lines matching /^\*\*(.+?)\*\*$/
 * - Within a group, non-empty lines are task items
 * - If the line ends with (MM:SS) or (H:MM:SS), strip and capture as ts/sec
 * - owner === "Unassigned" (case-insensitive) → unassigned: true
 * - Empty lines and groups with no tasks are skipped
 */
export function parseActionItems(source: string | null): ActionItemGroup[] {
	if (!source) return [];

	const lines = source.split("\n");
	const groups: ActionItemGroup[] = [];
	let currentOwner: string | null = null;
	let currentItems: ActionItemEntry[] = [];

	const flushGroup = () => {
		if (currentOwner !== null && currentItems.length > 0) {
			groups.push({
				owner: currentOwner,
				unassigned: currentOwner.toLowerCase() === "unassigned",
				items: currentItems,
			});
		}
		currentItems = [];
	};

	// Matches a trailing timestamp: (6:41), (06:41), (1:06:41)
	const TS_PATTERN = /\s*\((\d+:\d{2}(?::\d{2})?)\)\s*$/;

	for (const line of lines) {
		const trimmed = line.trim();
		if (!trimmed) continue;

		// Check if this line is a group header: **Owner Name**
		const ownerMatch = trimmed.match(/^\*\*(.+?)\*\*$/);
		if (ownerMatch) {
			flushGroup();
			currentOwner = (ownerMatch[1] ?? "").trim();
			continue;
		}

		// Only collect items if we have an active group
		if (currentOwner === null) continue;

		// Parse timestamp out of the task text
		const tsMatch = trimmed.match(TS_PATTERN);
		let text = trimmed;
		let ts: string | null = null;
		let sec: number | null = null;

		if (tsMatch) {
			text = trimmed.slice(0, trimmed.length - (tsMatch[0] ?? "").length).trim();
			ts = tsMatch[1] ?? null;
			if (ts) {
				// Convert to seconds: supports MM:SS and H:MM:SS
				const parts = ts.split(":").map(Number);
				if (parts.length === 2) {
					sec = (parts[0] ?? 0) * 60 + (parts[1] ?? 0);
				} else if (parts.length === 3) {
					sec = (parts[0] ?? 0) * 3600 + (parts[1] ?? 0) * 60 + (parts[2] ?? 0);
				}
			}
		}

		if (text) {
			currentItems.push({ text, ts, sec });
		}
	}

	flushGroup();
	return groups;
}

// ── Component ─────────────────────────────────────────────────────────────────

export interface ActionItemsProps {
	/** Raw action-items markdown string from the meeting metadata. */
	source: string | null;
	/** Called when the user clicks a timestamp pill. Arg is seconds from start. */
	onJump?: (sec: number) => void;
	/** Current user's display name for "You" badge detection (best-effort). */
	meName?: string | null;
}

/**
 * Renders the meeting action items as a grouped owner checklist with
 * jump-to-timestamp pills. Done state is local (UI-only, no persistence).
 */
export function ActionItems({ source, onJump, meName }: ActionItemsProps) {
	const groups = parseActionItems(source);

	// Local done state keyed `${groupIdx}-${itemIdx}` → boolean
	const [done, setDone] = useState<Record<string, boolean>>({});

	const total = groups.reduce((n, g) => n + g.items.length, 0);
	const doneCount = Object.values(done).filter(Boolean).length;

	const toggle = (key: string) => setDone((prev) => ({ ...prev, [key]: !prev[key] }));

	// Best-effort "me" detection: match on case-insensitive full name
	const isMe = (owner: string): boolean => {
		if (!meName) return false;
		return owner.toLowerCase() === meName.toLowerCase();
	};

	// "Copy all" — plain-text render of all groups and tasks
	const handleCopyAll = () => {
		const lines: string[] = [];
		for (const g of groups) {
			lines.push(g.owner);
			for (const item of g.items) {
				const pill = item.ts ? ` (${item.ts})` : "";
				lines.push(`  - ${item.text}${pill}`);
			}
			lines.push("");
		}
		navigator.clipboard.writeText(lines.join("\n").trimEnd()).then(
			() => toast.success("Copied"),
			() => toast.error("Copy failed"),
		);
	};

	if (groups.length === 0) {
		return <p className="text-body-sm text-ink-subtle">No action items recorded.</p>;
	}

	return (
		<div>
			{/* Section header */}
			<div className="mb-3 flex items-center justify-between">
				<div className="flex items-center gap-2">
					<span className="text-eyebrow uppercase text-ink-tertiary">Action items</span>
					<span className="text-meta tabular-nums text-ink-subtle">
						{doneCount}/{total} done
					</span>
				</div>
				<div className="flex items-center gap-1">
					<button
						type="button"
						onClick={handleCopyAll}
						className="flex h-7 items-center gap-1.5 rounded-md px-2 text-body-sm text-ink-subtle hover:text-ink"
					>
						<Copy className="size-3.5" />
						Copy all
					</button>
					{/* "Send to tasks" is intentionally omitted — no task integration exists */}
				</div>
			</div>

			{/* Owner groups */}
			<div className="space-y-3">
				{groups.map((g) => {
					const meGroup = isMe(g.owner);
					return (
						<div
							key={g.owner}
							className="overflow-hidden rounded-lg border border-hairline bg-surface-1"
						>
							{/* Group header */}
							<header className="flex items-center gap-2.5 border-b border-hairline px-4 py-2.5">
								{g.unassigned ? (
									<span className="flex size-7 flex-none items-center justify-center rounded-full border border-dashed border-hairline-strong text-ink-tertiary">
										<User className="size-3" />
									</span>
								) : (
									<Avatar
										name={g.owner}
										size="md"
										className={
											meGroup ? "ring-2 ring-primary ring-offset-1 ring-offset-canvas" : ""
										}
									/>
								)}
								<span
									className={`text-body-sm font-medium ${g.unassigned ? "text-ink-subtle" : "text-ink"}`}
								>
									{g.owner}
								</span>
								{meGroup && (
									<Badge tone="primary" dot="var(--color-primary)">
										You
									</Badge>
								)}
								<span className="ml-auto text-meta tabular-nums text-ink-subtle">
									{g.items.length} task{g.items.length !== 1 ? "s" : ""}
								</span>
							</header>

							{/* Task rows */}
							<ul className="divide-y divide-hairline">
								{g.items.map((item, ii) => {
									const key = `${g.owner}-${ii}`;
									const isDone = !!done[key];
									return (
										<li
											key={key}
											className="group flex items-start gap-3 px-4 py-2.5 hover:bg-surface-2/60"
										>
											{/* Checkbox button */}
											<button
												type="button"
												onClick={() => toggle(key)}
												className={`mt-0.5 flex size-[18px] flex-none items-center justify-center rounded-full border transition-colors ${
													isDone
														? "border-success bg-success text-canvas"
														: "border-hairline-strong text-transparent hover:border-primary"
												}`}
												aria-label={isDone ? "Mark incomplete" : "Mark complete"}
											>
												<Check className="size-3" />
											</button>

											{/* Task text */}
											<p
												className={`flex-1 text-body-sm leading-snug transition-colors ${
													isDone ? "text-ink-tertiary line-through" : "text-ink-muted"
												}`}
											>
												{item.text}
											</p>

											{/* Timestamp pill — only when a timestamp exists */}
											{item.ts !== null && item.sec !== null ? (
												<button
													type="button"
													onClick={() => {
														const sec = item.sec;
														if (sec !== null) onJump?.(sec);
													}}
													className="mt-px flex h-5 flex-none items-center gap-1 rounded border border-hairline bg-surface-2 px-1.5 font-mono text-meta text-ink-subtle transition-colors hover:border-primary/50 hover:text-ink"
													title={`Jump to ${item.ts}`}
												>
													<svg
														viewBox="0 0 12 12"
														className="size-2.5"
														fill="currentColor"
														aria-hidden="true"
													>
														<path d="M3 1.5v9l8-4.5L3 1.5Z" />
													</svg>
													{item.ts}
												</button>
											) : null}
										</li>
									);
								})}
							</ul>
						</div>
					);
				})}
			</div>
		</div>
	);
}
